import { useEffect, useState } from 'react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Money } from '../../components/Money';
import { fmtNum } from './utils';

const TREND_METRICS = [
  { key: 'revenue', label: 'Tutar', money: true },
  { key: 'routes', label: 'Rota' },
  { key: 'hours', label: 'Saat' },
  { key: 'stops', label: 'Stop' },
  { key: 'km', label: 'KM' },
];
const MONTHS_TR = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const monthLabel = (ym) => `${MONTHS_TR[Number(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}`;

function Mini({ label, children, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-base font-bold text-slate-900 dark:text-slate-100 tabular-nums">{children}</div>
    </div>
  );
}

function MetricTabs({ value, onChange, prefix }) {
  return (
    <div className="flex gap-1">
      {TREND_METRICS.map((m) => (
        <button key={m.key} type="button" onClick={() => onChange(m.key)} className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors ${value === m.key ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'}`} data-testid={`${prefix}-${m.key}`}>
          {m.label}
        </button>
      ))}
    </div>
  );
}

function DetailBody({ data }) {
  const [trend, setTrend] = useState('revenue');
  const [monthly, setMonthly] = useState('revenue');
  const s = data.summary;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-2">
        <Mini label="Rota" testId="dd-routes">{s.routes}</Mini>
        <Mini label="Gün" testId="dd-days">{s.days}</Mini>
        <Mini label="Saat" testId="dd-hours">{fmtNum(s.hours, 1)}</Mini>
        <Mini label="Tutar" testId="dd-revenue"><Money amount={s.revenue} /></Mini>
        <Mini label="Tutar / saat" testId="dd-rph"><Money amount={s.revenue_per_hour} /></Mini>
        <Mini label="Tutar / gün" testId="dd-rpd"><Money amount={s.revenue_per_day} /></Mini>
        <Mini label="Stop" testId="dd-stops">{s.stops ? `${fmtNum(s.stops)} · ⌀ ${fmtNum(s.stops_per_route, 1)}` : '—'}</Mini>
        <Mini label="KM" testId="dd-km">{s.km ? `${fmtNum(s.km)} · ⌀ ${fmtNum(s.km_per_route, 1)}` : '—'}</Mini>
        <Mini label="Rota / gün" testId="dd-rpday">{fmtNum(s.routes_per_day, 2)}</Mini>
      </div>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold">Günlük trend</h4>
          <MetricTabs value={trend} onChange={setTrend} prefix="dd-trend" />
        </div>
        <div className="h-44" data-testid="dd-trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.daily} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <defs><linearGradient id="ddg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.35} /><stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmtNum(v)} />
              <Tooltip formatter={(v) => fmtNum(v, 2)} />
              <Area type="monotone" dataKey={trend} stroke="#0ea5e9" fill="url(#ddg)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold">Son 12 ay</h4>
          <MetricTabs value={monthly} onChange={setMonthly} prefix="dd-month" />
        </div>
        <div className="h-44" data-testid="dd-monthly-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.monthly} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={monthLabel} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmtNum(v)} />
              <Tooltip labelFormatter={monthLabel} formatter={(v) => fmtNum(v, 2)} />
              <Bar dataKey={monthly} fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <table className="w-full text-xs mt-2" data-testid="dd-monthly-table">
          <thead className="text-slate-400"><tr><th className="text-left py-1">Ay</th><th className="text-right">Rota</th><th className="text-right">Gün</th><th className="text-right">Saat</th><th className="text-right">Tutar</th><th className="text-right">€/saat</th></tr></thead>
          <tbody>
            {[...data.monthly].reverse().filter((m) => m.routes > 0).map((m) => (
              <tr key={m.month} className="border-t border-slate-100 dark:border-slate-800 tabular-nums">
                <td className="py-1">{monthLabel(m.month)}</td><td className="text-right">{m.routes}</td><td className="text-right">{m.days}</td>
                <td className="text-right">{fmtNum(m.hours, 1)}</td><td className="text-right"><Money amount={m.revenue} /></td><td className="text-right"><Money amount={m.revenue_per_hour} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h4 className="text-sm font-semibold mb-2">Günlük rotalar ({data.routes.length})</h4>
        <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-xs" data-testid="dd-routes-table">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 text-slate-500"><tr><th className="text-left px-2 py-1.5">Tarih</th><th className="text-left">Müşteri</th><th className="text-left">Route</th><th className="text-left">Plaka</th><th className="text-right">Saat</th><th className="text-right">Stop</th><th className="text-right">KM</th><th className="text-right px-2">Tutar</th></tr></thead>
            <tbody>
              {data.routes.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800 tabular-nums">
                  <td className="px-2 py-1 whitespace-nowrap">{r.date}</td>
                  <td className="uppercase text-[10px] text-slate-500">{r.customer}</td>
                  <td>{r.route_number || r.plaats || r.route_type || r.rit_number || '—'}</td>
                  <td>{r.license_plate || ''}</td>
                  <td className="text-right whitespace-nowrap">{r.start_time && r.end_time ? `${r.start_time}–${r.end_time}` : fmtNum(r.work_hours, 1)}</td>
                  <td className="text-right">{r.stops || '—'}</td>
                  <td className="text-right">{r.km_total ? fmtNum(r.km_total) : '—'}</td>
                  <td className="text-right px-2"><Money amount={r.total_cost} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

// Sağdan açılan şoför detay paneli.
export function DriverDetailSheet({ driver, params, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!driver) return;
    setLoading(true);
    setData(null);
    api.get('/routes/driver-analytics/detail', { params: { ...params, driver_key: driver.driver_key } })
      .then((r) => setData(r.data?.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [driver, params]);

  return (
    <Sheet open={!!driver} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="driver-detail-sheet">
        <SheetHeader>
          <SheetTitle data-testid="driver-detail-name">{driver?.driver_name}</SheetTitle>
          <p className="text-xs text-slate-500">{params.start_date} – {params.end_date} · {driver?.customers?.join(' · ')}</p>
        </SheetHeader>
        <div className="mt-4">
          {loading && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
          {!loading && data && <DetailBody data={data} />}
          {!loading && !data && driver && <p className="text-sm text-slate-400 py-10 text-center">Detay yüklenemedi</p>}
        </div>
      </SheetContent>
    </Sheet>
  );
}
